import React from "react";
import intl from "react-intl-universal";

import {TableDataSource} from "../dataSources/TableDataSource";
import {FilterSchema} from "../dataSources/DataSource";
import {ColId, ColSchema} from "../../ui/components/Table";
import {GQLResponse} from "../gqlModel/types";
import {BasicDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";
import {queryGqlApi} from "../../graphql/queryGqlApi";
import {listAudits} from "../../graphql/audit/queries";
import {onCreateAuditTemplate, onUpdateAuditTemplate, onDeleteAuditTemplate} from "../../graphql/audit/subscriptions";
import {InTextLabel} from "../../ui/components/InTextLabel/InTextLabel";
import {AuditTemplate} from "./types";
import {AuditRowActions} from "../../ui/screens/Audits/AuditRowActions";

const ENDPOINT = "pgServer";

const colOrder: ColId[] = ['name', 'questionsCount', 'passingPct', 'publication_state', 'actions'];

const colSchema: ColSchema = {
    name: {
        getTitle: () => intl.get('audits.list.colAudit').d('Audit'),
        alignLeft: true,
        minWidth: 200,
    },
    questionsCount: {
        getTitle: () => intl.get('audits.list.colQuestions').d('Questions'),
        minWidth: 80,
        maxWidth: 120,
    },
    passingPct: {
        getTitle: () => intl.get('audits.list.colPassing').d('Passing'),
        minWidth: 80,
        maxWidth: 120,
    },
    publication_state: {
        getTitle: () => intl.get('audits.list.colPublication').d('Publication'),
        minWidth: 120,
        maxWidth: 180,
    },
    actions: {
        getTitle: () => '',
        minWidth: 128,
        maxWidth: 128,
    },
};

export class AuditTemplateDS extends TableDataSource<AuditTemplate> {
    readonly _modelName = 'AuditTemplate' as const;
    readonly wsId: string;

    readonly _subscriptionGQLs = {
        onCreate: onCreateAuditTemplate,
        onUpdate: onUpdateAuditTemplate,
        onDelete: onDeleteAuditTemplate,
    };

    /** Set from the screen to handle the Run button click in the actions column. */
    onRunClick?: (template: AuditTemplate) => void;

    constructor(deps: BasicDataSourceDeps, wsId: string) {
        super(deps);
        this.wsId = wsId;
        this.setSortRule('name');
    }

    get syncHash() { return this.wsId; }

    get syncQueryFilter() { return {workspaceId: this.wsId}; }

    getFilterSchema(): FilterSchema<AuditTemplate> {
        return {
            name: {
                type: 'client',
                apply: (t) => t.name.toLowerCase().includes((this.filterValues.name ?? '').toLowerCase()),
            },
        };
    }

    getFilterDefaultValues() { return {name: ''}; }

    loadGQLFetch(): Promise<GQLResponse<AuditTemplate>> {
        return queryGqlApi(
            listAudits, {workspaceId: this.wsId},
            this.deps.dataClientService, this.deps.authService, ENDPOINT,
        ) as Promise<GQLResponse<AuditTemplate>>;
    }

    loadMoreGQLFetch(nextToken: string): Promise<GQLResponse<AuditTemplate>> {
        return queryGqlApi(
            listAudits, {workspaceId: this.wsId, nextToken},
            this.deps.dataClientService, this.deps.authService, ENDPOINT,
        ) as Promise<GQLResponse<AuditTemplate>>;
    }

    getWsClient() {
        return this.deps.dataClientService.getWsClientByKey(ENDPOINT);
    }

    getColsSchema(): ColSchema { return colSchema; }
    getColOrder(): ColId[] { return colOrder; }

    mapResultToTableRow = (template: AuditTemplate) => {
        const published = template.publicationState === 'published';
        return {
            ...template,
            name: template.name || intl.get('audits.list.untitled').d('Untitled'),
            passingPct: `${template.requiredPassPercentage}%`,
            publication_state: published
                ? <InTextLabel type="success" size="sm" text={intl.get('audits.status.published').d('Published')}/>
                : <InTextLabel type="neutral" size="sm" text={intl.get('audits.status.draft').d('Draft')}/>,
            actions: <AuditRowActions template={template} onRunClick={this.onRunClick}/>,
            raw: template,
        };
    };

    /**
     * Manual optimistic update for callers that just ran a mutation. Subscriptions
     * keep this in sync on a live server, but the mock backend never fires them
     * (see resolversReliabilityAudit.ts), so action call sites must call this too.
     */
    upsertAuditTemplate(template: AuditTemplate) {
        this._upsertItem(template, true);
    }

    removeAuditTemplate(id: string) {
        this._removeItem(id);
    }
}
